import React, { useMemo, useState } from 'react'
import { Typography, Table, Tooltip, Tag, Select } from 'antd'
import {
	InfoCircleOutlined,
	ExclamationCircleOutlined
} from '@ant-design/icons'
import { isMobile } from 'react-device-detect'
import { observer } from 'mobx-react-lite'

import { useStores } from '../../../cls/stores/MobXProvider'
import { useSmartTranslation } from '../../../cls/TranslationContext'
import { runInAction, toJS } from 'mobx'
import { actSaveUser } from '../../../stores/users/actions'

const { Title } = Typography

function ActivePurchases() {
	/* ---------- stores ---------- */
	const { users, subscriptions } = useStores()
	const { t } = useSmartTranslation()
	const [dinamicUpdateKey, setDinamicUpdateKey] = useState(0)
	const plans = subscriptions.plans // recurring price cache

	const { loggedInUserId, loggedInUser } = users
	const forUserId = loggedInUserId
	// is super admin
	const isSuperAdmin = loggedInUser?.is_super_admin || false

	const user = users.requireUserById(forUserId)
	const userPurchases = user.temporaryPurchases || []

	/* ---------- local state ---------- */
	const [statusFilter, setStatusFilter] = useState('all')

	/* ---------- derived list ---------- */
	const filteredPurchases = useMemo(() => {
		if (statusFilter === 'all') return userPurchases
		return userPurchases.filter((p) => p.payment_status === statusFilter)
	}, [userPurchases, statusFilter])

	const statusOptions = [
		{ value: 'all', label: t('single.status-all') },
		{ value: 'paid', label: t('single.paid') },
		{ value: 'unpaid', label: t('single.unpaid') },
		{
			value: 'no_payment_required',
			label: t('single.no-payment-required')
		},
		{ value: 'refunded', label: t('single.refunded') }
	]

	/* ---------- helpers ---------- */
	const formatPrice = (amount, currency) =>
		`${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`

	const updatePurchaseStatus = async (purchaseId, newStatus) => {
		runInAction(() => {
			user.setPurchaseStatus(purchaseId, newStatus)
			actSaveUser(user.user_id, toJS(user.full), toJS(user.allowed)).then(
				() => {
					users.saveState()
				}
			)
			setDinamicUpdateKey((prev) => prev + 1) // force re-render
		})
	}

	const columns = [
		{
			/* 📦 Product column – resolves name & details from the cached `plans` */
			title: t('single.product'),
			dataIndex: ['metadata', 'price_id'], // grabs the Price-ID
			key: 'id',
			render: (id) => {
				const plan = plans.find((p) => p.id === id)
				if (!plan) return id // fallback

				const { name, description, metadata } = plan.product
				const planType = metadata?.type || 'N/A'
				const planCategory = metadata?.category || 'N/A'
				const unitAmount = plan.unit_amount
				const currency = plan.currency?.toUpperCase()

				return (
					<Tooltip
						title={
							<div>
								<p>
									<strong>{t('single.type')}</strong>{' '}
									{planType}
								</p>
								<p>
									<strong>{t('single.category')}</strong>{' '}
									{planCategory}
								</p>

								{description && (
									<p>
										<strong>
											{t('single.description')}
										</strong>{' '}
										{description}
									</p>
								)}

								{unitAmount && (
									<p>
										<strong>{t('single.value')}</strong>{' '}
										{`${(unitAmount / 100).toFixed(
											2
										)} ${currency}`}
									</p>
								)}
							</div>
						}
					>
						<span>
							<InfoCircleOutlined style={{ marginRight: 5 }} />
							{name}
						</span>
					</Tooltip>
				)
			}
		},
		{
			title: t('single.status'),
			dataIndex: 'payment_status',
			key: 'status',
			render: (status) => (
				<Tag color={status === 'paid' ? 'green' : 'orange'}>
					{status}
				</Tag>
			)
		},
		{
			title: t('single.value'),
			dataIndex: 'amount_total',
			key: 'amount',
			render: (amt, rec) => formatPrice(amt, rec.currency)
		},
		{
			title: t('single.date'),
			dataIndex: 'created',
			key: 'date',
			render: (ts) => new Date(ts * 1000).toLocaleDateString()
		},
		{
			title: t('single.fulfillment'),
			key: 'fulfilled',
			dataIndex: ['metadata'],
			render: (_, record) => {
				const isFulfilled =
					user.purchasesHistory[record.id]?.status === 'completed'

				const isRefunded =
					record?.payment_intent?.latest_charge?.refunded

				return isRefunded ? (
					<Tag color="red">{t('single.refunded')}</Tag>
				) : isFulfilled ? (
					<Tag
						style={isSuperAdmin ? { cursor: 'pointer' } : {}}
						onClick={
							isSuperAdmin
								? () =>
										updatePurchaseStatus(
											record.id,
											'pending'
										)
								: undefined
						}
						color="green"
					>
						{t('single.completed')}
					</Tag>
				) : (
					<Tag
						style={isSuperAdmin ? { cursor: 'pointer' } : {}}
						onClick={
							isSuperAdmin
								? () =>
										updatePurchaseStatus(
											record.id,
											'completed'
										)
								: undefined
						}
					>
						{t('single.pending')}
					</Tag>
				)
			}
		}
	]

	/* ---------- ui ---------- */
	return (
		<>
			<Select
				style={{ width: 200, marginBottom: 20 }}
				placeholder={t('__filter-by-x%single.status')}
				options={statusOptions}
				value={statusFilter}
				onChange={setStatusFilter}
			/>

			{filteredPurchases.length ? (
				<Table
					key={dinamicUpdateKey} // force re-render on status change
					columns={columns}
					dataSource={filteredPurchases}
					rowKey="id"
					pagination={false}
					scroll={{ x: isMobile && 'max-content' }}
				/>
			) : (
				<Title level={4}>
					<ExclamationCircleOutlined /> {t('single.no-purchases')}
				</Title>
			)}
		</>
	)
}

export default observer(ActivePurchases)
